// src/pages/Dictionaries.jsx
import { useEffect, useRef, useState } from "react";
import {
  Box, Stack, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, LinearProgress,
  Menu, MenuItem, ListItemIcon, ListItemText, TableContainer, Paper, DialogContentText
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// import { AddCircleOutline } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  listDictionaries, createDictionary, deleteDictionary, updateDictionary
} from "../utils/api";
import { formatDistanceToNow, format } from "date-fns";

// Header icons
import { ReactComponent as BookIcon } from "../assets/icons/book.svg";
import { ReactComponent as ClockIcon } from "../assets/icons/clock.svg";
import { ReactComponent as FileIcon } from "../assets/icons/file.svg";

// Action menu icons
import { ReactComponent as AddIcon } from "../assets/icons/plus.svg";
import { ReactComponent as MoreHorizIcon } from "../assets/icons/dots-horizontal.svg";
import { ReactComponent as CloudUpload } from "../assets/icons/upload.svg";
import { ReactComponent as OpenIcon } from "../assets/icons/link-external.svg";
import { ReactComponent as EditIcon } from "../assets/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../assets/icons/trash.svg";

import { ReactComponent as SnowflakeIcon } from "../assets/icons/snowflake.svg";
// import { ReactComponent as BigqueryIcon } from "../assets/icons/bigquery.svg";
import { ReactComponent as DatabricksIcon } from "../assets/icons/databricks.svg";
import { ReactComponent as RedshiftIcon } from "../assets/icons/redshift.svg";


import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";



export default function Dictionaries() {
  const nav = useNavigate();
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // rename dialog state
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [savingRename, setSavingRename] = useState(false);

  // per-row actions menu state
  const [menuAnchor, setMenuAnchor] = useState(null); // HTMLElement | null
  const [menuRow, setMenuRow] = useState(null);       // the row object for which menu is open


  const [importAnchor, setImportAnchor] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
    
  const load = async () => {
    setLoading(true);
    try {
      const data = await listDictionaries();
      data.sort((a, b) => (b.created_at?._seconds ?? 0) - (a.created_at?._seconds ?? 0));
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // immediate upload when a file is chosen
  const onPickAndUpload = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    try {
      setUploading(true);
      await createDictionary({ file: picked, name: picked.name });
      await load();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    } finally {
      // reset input so picking same file again will retrigger
      e.target.value = "";
      setUploading(false);
    }
  };

  // const onDelete = async (id) => {
  //   if (!window.confirm("Delete this dictionary? This cannot be undone.")) return;
  //   try {
  //     setLoading(true);
  //     await deleteDictionary(id);
  //     await load();
  //   } catch (e) {
  //     console.error(e);
  //     alert("Delete failed.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // actions menu handlers
  const openMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const actionOpen = () => {
    if (menuRow) nav(`/dictionaries/${menuRow.id}`);
    closeMenu();
  };
  const actionRename = () => {
    if (menuRow) setRenaming({ id: menuRow.id, name: menuRow.name || menuRow.filename || "" });
    closeMenu();
  };
  // const actionDelete = async () => {
  //   if (menuRow) await onDelete(menuRow.id);
  //   closeMenu();
  // };

    const handleDeleteClick = (row) => {
      closeMenu();
      setDeleteTarget(row);
      setConfirmOpen(true);
    };
    
    const handleDeleteConfirm = async () => {
      if (!deleteTarget) return;
      try {
        setLoading(true);
        await deleteDictionary(deleteTarget.id);
        await load(); // ✅ refresh immediately
      } catch (e) {
        console.error(e);
        alert("Delete failed.");
      } finally {
        setConfirmOpen(false);
        setDeleteTarget(null);
        setLoading(false);
      }
    };
    
    const handleDeleteCancel = () => {
      setConfirmOpen(false);
      setDeleteTarget(null);
    };

  const saveRename = async () => {
    try {
      setSavingRename(true);
      await updateDictionary(renaming.id, { name: renaming.name });
      setRenaming(null);
      await load();
    } catch (e) {
      console.error(e);
      alert("Rename failed.");
    } finally {
      setSavingRename(false);
    }
  };


    
  return (
    <Box>
      {/* Header row */}
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
    >
      <Typography
        variant="subtitle1"
        sx={{ typography: theme.typography.subtitle1 }}
      >
        Data Dictionaries
      </Typography>
    
      <Stack direction="row" gap={1.5} alignItems="center">
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={onPickAndUpload}
        />

        {/* Create Button */}
        <Button
          variant="contained"
          color="white"
          onClick={() => nav("/dictionaries/new")}
          disabled={uploading}
          startIcon={
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                svg: {
                  width: 16,
                  height: 16,
                  stroke: "currentColor",
                  fill: "none",
                  color: "inherit",
                  "& path": {
                    stroke: "currentColor !important",
                    fill: "none !important",
                  },
                },
              }}
            >
              <AddIcon />
            </Box>
          }
          sx={{
            typography: theme.typography.paragraph,
            textTransform: "none",
            fontWeight: 500,
            color: theme.palette.text.primary,
            backgroundColor: "#FFF",
            border: "1px solid #ECF2F8",
            borderRadius: 1.5,
            boxShadow: "none",
            px: 2.5,
            py: 0.9,
            "&:hover": {
              backgroundColor: "#F9FBFC",
              borderColor: "#DCE6EE",
              boxShadow: "none",
            },
            "& .MuiButton-startIcon": {
              marginRight: 1,
              marginLeft: 0,
            },
          }}
        >
          Create
        </Button>
    
        
        {/* Import Dropdown */}
        <Button
          variant="contained"
          color="white"
          onClick={(e) => setImportAnchor(e.currentTarget)}
          disabled={uploading}
          endIcon={<ArrowDropDownIcon />}
          startIcon={
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                svg: {
                  width: 16,
                  height: 16,
                  stroke: "currentColor",
                  fill: "none",
                  color: "inherit",
                },
              }}
            >
              <CloudUpload />
            </Box>
          }
          sx={{
            typography: theme.typography.paragraph,
            textTransform: "none",
            fontWeight: 500,
            color: theme.palette.text.primary,
            backgroundColor: "#FFF",
            border: "1px solid #ECF2F8",
            borderRadius: 1.5,
            boxShadow: "none",
            px: 2.5,
            py: 0.9,
            "&:hover": {
              backgroundColor: "#F9FBFC",
              borderColor: "#DCE6EE",
              boxShadow: "none",
            },
          }}
        >
          Import from Platform
        </Button>
        
        <Menu
          anchorEl={importAnchor}
          open={Boolean(importAnchor)}
          onClose={() => setImportAnchor(null)}
        >
          <MenuItem onClick={() => { setImportAnchor(null); nav("/dictionaries/import-snowflake"); }}>
            <ListItemIcon
              sx={{
                color: "inherit", 
                "& svg path": {
                  fill: "#29B5E8 !important",
                },
              }}
            >
              <SnowflakeIcon width={18} height={18} />
            </ListItemIcon>
            <ListItemText>Snowflake</ListItemText>
          </MenuItem>
          {/* <MenuItem disabled>
            <ListItemIcon><BigqueryIcon width={16} height={16} /></ListItemIcon>
            <ListItemText>BigQuery (coming soon)</ListItemText>
          </MenuItem> */}
          <MenuItem disabled>
            <ListItemIcon><DatabricksIcon width={16} height={16} /></ListItemIcon>
            <ListItemText>Databricks (coming soon)</ListItemText>
          </MenuItem>
          <MenuItem disabled>
            <ListItemIcon><RedshiftIcon width={16} height={16} /></ListItemIcon>
            <ListItemText>Redshift (coming soon)</ListItemText>
          </MenuItem>
        </Menu>
          
      </Stack>
    </Stack>
    
    {/* Loading indicator — now separate and visually below header */}
    {loading && <LinearProgress sx={{ mb: 2 }} />}


        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid #eee", borderRadius: 2, bgcolor: "grey.50" }}
        >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BookIcon width={16} height={16} />
                <Typography variant="body2">Name</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FileIcon width={16} height={16} />
                <Typography variant="body2">Filename</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ClockIcon width={16} height={16} />
                <Typography variant="body2">Created</Typography>
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ClockIcon width={16} height={16} />
                <Typography variant="body2">Updated</Typography>
              </Box>
            </TableCell>
            <TableCell align="right">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                <Typography variant="body2"></Typography>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(row => (
            <TableRow key={row.id} hover>
              {/* Make name act like a link to the editor */}
              <TableCell
                onClick={() => nav(`/dictionaries/${row.id}`)}
                sx={{ cursor: "pointer", color: "primary.main", fontWeight: 500 }}
              >
                {row.name || row.filename || "-"}
              </TableCell>

              <TableCell>{row.filename || "-"}</TableCell>
              <TableCell>{formatTime(row.created_at)}</TableCell>
              <TableCell>{formatTime(row.updated_at)}</TableCell>

              <TableCell align="right">
                <IconButton size="small" onClick={(e) => openMenu(e, row)} aria-label="More actions">
                  <MoreHorizIcon fontSize="inherit" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && !loading && (
            <TableRow><TableCell colSpan={5}>No dictionaries yet.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
        </TableContainer>

      {/* Per-row menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={actionOpen}>
          <ListItemIcon>
            <OpenIcon width={16} height={16} />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>
        <MenuItem onClick={actionRename}>
          <ListItemIcon>
            <EditIcon width={16} height={16} />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        {/* <MenuItem onClick={actionDelete}> */}
        <MenuItem onClick={() => handleDeleteClick(menuRow)}>
          <ListItemIcon>
            <DeleteIcon width={16} height={16} />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
        </MenuItem>
      </Menu>


      <Dialog open={confirmOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Delete Dictionary</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete “{deleteTarget?.name || deleteTarget?.filename}”?
              This action cannot be undone. 
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        
      {/* Rename dialog */}
      <Dialog open={!!renaming} onClose={() => setRenaming(null)}>
        <DialogTitle>Rename dictionary</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Name"
            value={renaming?.name || ""}
            onChange={(e) => setRenaming(s => ({ ...s, name: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button onClick={saveRename} disabled={savingRename} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// --- normalize Firestore timestamp shapes into millis ---
function toMillis(v) {
  if (!v) return 0;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof v === "number") {
    // assume seconds if small, ms if large
    return v > 1e12 ? v : v * 1000;
  }
  const sec = v._seconds ?? v.seconds ?? null;
  if (sec != null) return sec * 1000;
  if (typeof v.toDate === "function") {
    try { return v.toDate().getTime(); } catch {}
  }
  return 0;
}

// --- your formatter ---
function formatTime(createdAt) {
  const ms = toMillis(createdAt);
  if (!ms) return "-";
  const date = new Date(ms);
  const now = new Date();
  const diffHours = (now - date) / (1000 * 60 * 60);
  if (diffHours < 24) return formatDistanceToNow(date, { addSuffix: true });
  return format(date, "M/d/yyyy, h:mm a");
}